import React, { useState, useEffect, useMemo } from "react";
import ListBox from "../../common/ListBox/ListBox";
import PageBreadcrumb from "../../common/PageBreadcrumb/PageBreadcrumb";
//loader and alerts
import { useLoader } from "../../../context/LoaderContext";
import useInfoAlert from "../../../hooks/useInfoAlert";
import InfoAlert from "../../common/InfoAlert";
//user session data
import { useUserData } from "../../../hooks/useUserData";
//api usage
import api from "../../../services/apiClient";
import API_ENDPOINTS from "../../../config/apiEndpoints";
import { filterAssignablePermissionKeys } from "../../../constants/permissions";
import {
  getAllPermissionKeys,
  getEffectivePermissionKeys,
  isSystemAdministratorRole,
  isHiddenFromRoleManagement,
} from "../../../utils/permissionUtils";
import { expandStoredQmsPermissionKeys } from "../qms/qmsPermissionUtils";
import { expandStoredIsfPermissionKeys } from "../../../constants/isfPermissionConsolidation";
import { sanitizeSelectedPermissionsForSave } from "../../../constants/qmsPermissions";
import { buildRolePermissionSummary } from "../../../utils/rolePermissionSummary";
import UserRolePermissionPanel from "./UserRolePermissionPanel";
import RolePermissionSummary from "./RolePermissionSummary";
import ConfirmModal from "../../common/ConfirmModal";
import { openAuthModal } from "../../common/authmodal/authmodalservice";
import "./RoleManagement.scss";

function normalizeRoleName(name) {
  return String(name || "")
    .replace(/[\u00a0\u2000-\u200b\t\r\n]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function mapAssignedStaffRows(rows) {
  return (rows || [])
    .map((row) => ({
      staffId: row.StaffId,
      name: String(row.UserName || "").replace(/\s+/g, " ").trim(),
      email: String(row.EmailId || "").trim(),
    }))
    .filter((row) => row.name);
}

const RoleManagement = () => {
  const { userData } = useUserData();
  const { showLoader, hideLoader } = useLoader();
  const { alertState, showAlert, hideAlert } = useInfoAlert();

  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState({});
  const [selectedPermissions, setSelectedPermissions] = useState([]);

  const selectedRoleOption = useMemo(
    () =>
      selectedRole?.id
        ? { value: selectedRole.id, label: selectedRole.title }
        : null,
    [selectedRole?.id, selectedRole?.title],
  );

  const roleOptions = useMemo(
    () =>
      (roles || []).map((role) => ({
        value: role.id,
        label: role.title,
      })),
    [roles],
  );

  const isSystemAdministratorSelectedRole = isSystemAdministratorRole(
    selectedRole?.title,
  );

  const canEditRolePermissions =
    Boolean(selectedRole?.id) && !isSystemAdministratorSelectedRole;

  const roleSummary = useMemo(
    () =>
      buildRolePermissionSummary({
        userData,
        selectedPermissions,
        roleName: selectedRole?.title || "",
        isFullAccess: isSystemAdministratorSelectedRole,
      }),
    [
      userData,
      selectedPermissions,
      selectedRole?.title,
      isSystemAdministratorSelectedRole,
    ],
  );

  useEffect(() => {
    if (userData) {
      getRolesByOrganization("");
    }
  }, [userData]);

  useEffect(() => {
    if (!selectedRole?.id) {
      setSelectedPermissions([]);
      return;
    }
    setSelectedPermissions(
      filterAssignablePermissionKeys(
        expandStoredIsfPermissionKeys(
          expandStoredQmsPermissionKeys(
            getEffectivePermissionKeys(selectedRole.title, selectedRole.permission),
          ),
        ),
      ),
    );
  }, [selectedRole?.id, selectedRole?.title, selectedRole?.permission]);

  const getRolesByOrganization = async (input, input2) => {
    try {
      showLoader("Loading roles...");

      const response = await api.post(API_ENDPOINTS.ROLES.StaffRolesActivity, {
        OrganizationId: userData.OrganizationId,
        CreatedBy: userData.StaffId,
        ActionType: "GetStaffRoles",
        Roles: "",
        RoleId: "",
      });
      if (response?.success === 1) {
        const mappedRoles = (response.data.SuccessStory || [])
          .map((item, index) => ({
            id: item.RoleId || index,
            title: normalizeRoleName(item.Roles),
            subTitle: `${item.CreatedBy?.trim()} • ${item.SystemDateTime}`,
            dataType: "General",
            permission: item.Permission,
          }))
          .filter((item) => !isHiddenFromRoleManagement(item.title));
        const matchName = String(input2 || "")
          .trim()
          .replace(/\s+/g, " ")
          .toLowerCase();
        let nextSelected = mappedRoles[0];
        if (input === "newRole") {
          nextSelected =
            mappedRoles.find(
              (item) =>
                String(item.title || "")
                  .trim()
                  .replace(/\s+/g, " ")
                  .toLowerCase() === matchName,
            ) || mappedRoles[0];
        } else if (input) {
          nextSelected =
            mappedRoles.find((item) => item.id === input) || mappedRoles[0];
        }
        setSelectedRole(nextSelected || {});
        setRoles(mappedRoles);
      }
    } catch (error) {
      console.error("Error loading roles", error);
    } finally {
      hideLoader();
    }
  };

  const [roleToDelete, setRoleToDelete] = useState(null);
  const [assignedUsersAlert, setAssignedUsersAlert] = useState(null);

  const handleListBoxSelect = (node) => {
    setSelectedRole(node || {});
  };

  //role modal
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [isNewRole, setIsNewRole] = useState(false);
  const [roleName, setRoleName] = useState("");
  const [roleNameError, setRoleNameError] = useState("");

  const openAddRoleModal = () => {
    setIsNewRole(true);
    setRoleName("");
    setRoleNameError("");
    setShowRoleModal(true);
  };

  const openEditRoleModal = (role) => {
    if (!role?.id) return;
    setSelectedRole(role);
    setIsNewRole(false);
    setRoleName(role.title || "");
    setRoleNameError("");
    setShowRoleModal(true);
  };

  const closeRoleModal = () => {
    setShowRoleModal(false);
    setRoleName("");
    setRoleNameError("");
  };

  const handleRoleAction = (payload) => {
    const action = payload?.action;
    const item = payload?.item;
    if (!item) return;
    if (action === "edit") {
      openEditRoleModal(item);
      return;
    }
    if (action === "delete") {
      if (isHiddenFromRoleManagement(item.title)) {
        showAlert({
          title: "Cannot delete",
          message: "This role cannot be deleted.",
          type: "warning",
        });
        return;
      }
      checkRoleBeforeDelete(item);
    }
  };

  const closeAssignedUsersAlert = () => {
    setAssignedUsersAlert(null);
  };

  const checkRoleBeforeDelete = async (role) => {
    if (!role?.id) return;
    try {
      showLoader("Checking who uses this role...");
      const response = await api.post(API_ENDPOINTS.ROLES.StaffRolesActivity, {
        OrganizationId: userData.OrganizationId,
        CreatedBy: userData.StaffId,
        ActionType: "GetStaffAssignedToRole",
        Roles: "",
        RoleId: role.id,
        Permission: "",
      });
      const rows = response?.data?.SuccessStory || [];
      if (response?.success !== 1) {
        showAlert({
          title: "Error",
          message: rows[0]?.MSG || "Could not check who is assigned to this role.",
          type: "danger",
        });
        return;
      }
      const users = mapAssignedStaffRows(rows);
      if (users.length) {
        setAssignedUsersAlert({ role, users });
        return;
      }
      setRoleToDelete(role);
    } catch (error) {
      console.error("Error checking role assignments", error);
      showAlert({
        title: "Error",
        message: "Could not check who is assigned to this role. Please try again.",
        type: "danger",
      });
    } finally {
      hideLoader();
    }
  };

  const closeDeleteRoleModal = () => {
    setRoleToDelete(null);
  };

  const handleDeleteRole = async () => {
    const role = roleToDelete;
    if (!role?.id) return;
    closeDeleteRoleModal();

    const authRes = await openAuthModal({
      showDescription: false,
      introMessage: "Confirm your identity to delete this role.",
    });
    if (!authRes || authRes.Success !== 1) return;

    try {
      showLoader("Deleting role...");
      const response = await api.post(API_ENDPOINTS.ROLES.StaffRolesActivity, {
        OrganizationId: userData.OrganizationId,
        CreatedBy: userData.StaffId,
        ActionType: "DeleteStaffRole",
        Roles: "",
        RoleId: role.id,
        Permission: "",
      });
      const result = response?.data?.SuccessStory?.[0];
      const didSucceed =
        result?.Success === 1 ||
        result?.Success === true ||
        result?.MSG === "Role has been deleted successfully";
      if (didSucceed) {
        showAlert({
          title: "Success",
          message: result?.MSG || "Role has been deleted successfully",
          type: "success",
        });
        getRolesByOrganization("");
      } else {
        showAlert({
          title: "Cannot delete",
          message:
            result?.MSG ||
            "This role cannot be deleted while it is assigned to someone.",
          type: "warning",
        });
      }
    } catch (error) {
      console.error("Error deleting role", error);
      showAlert({
        title: "Error",
        message: "The role could not be deleted. Please try again.",
        type: "danger",
      });
    } finally {
      hideLoader();
    }
  };
  const handleRole = async () => {
    const trimmedName = normalizeRoleName(roleName);
    if (!trimmedName) {
      setRoleNameError("Enter a role name.");
      return;
    }
    if (trimmedName !== roleName) {
      setRoleName(trimmedName);
    }
    if (isHiddenFromRoleManagement(trimmedName)) {
      setRoleNameError(
        "Sponsor and CRO are reserved names. You cannot create a role with this name.",
      );
      return;
    }
    try {
      showLoader("Saving role");

      const response = await api.post(API_ENDPOINTS.ROLES.StaffRolesActivity, {
        OrganizationId: userData.OrganizationId,
        CreatedBy: userData.StaffId,
        ActionType: isNewRole ? "AddStaffRoles" : "UpdateStaffRole",
        Roles: trimmedName,
        RoleId: isNewRole ? "" : selectedRole?.id,
        Permission: isNewRole ? "" : selectedPermissions.join(","),
      });
      if (
        (response?.success === 1 &&
          response.data.SuccessStory[0].MSG ==
            "Role has been saved successfully") ||
        response.data.SuccessStory[0].MSG ==
          "Role has been updated successfully"
      ) {
        showAlert({
          title: "Success",
          message: response.data.SuccessStory[0].MSG,
          type: "success",
        });
        getRolesByOrganization("newRole", trimmedName);
        closeRoleModal();
      } else {
        setRoleNameError(
          response.data.SuccessStory[0].MSG || "This role could not be saved.",
        );
      }
    } catch (error) {
      console.error("Error saving roles", error);
      setRoleNameError("This role could not be saved. Please try again.");
    } finally {
      hideLoader();
    }
  };

  const handlePermissionsChange = (permissions) => {
    setSelectedPermissions(filterAssignablePermissionKeys(permissions || []));
  };

  const handleSavePermissions = async () => {
    const permissionPayload = isSystemAdministratorRole(selectedRole?.title)
      ? getAllPermissionKeys().join(",")
      : sanitizeSelectedPermissionsForSave(selectedPermissions).join(",");
    //UpdatePermission
    // 🔹 API CALL HERE
    try {
      showLoader("Saving permissions");

      const response = await api.post(API_ENDPOINTS.ROLES.StaffRolesActivity, {
        OrganizationId: userData.OrganizationId,
        CreatedBy: userData.StaffId,
        ActionType: "UpdatePermission",
        Roles: "",
        RoleId: selectedRole?.id,
        Permission: permissionPayload,
      });
      if (response?.success === 1) {
        getRolesByOrganization(selectedRole?.id);
        showAlert({
          title: "Success",
          message: response.data.SuccessStory[0].MSG,
          type: "success",
        });
      }
    } catch (error) {
      console.error("Error loading roles", error);
    } finally {
      hideLoader();
    }
  };

  return (
    <>
      <div className="role-mgmt-page">
        <PageBreadcrumb
          items={[
            { label: "Home", path: "/notification-center" },
            { label: "Role Management" },
          ]}
          actions={[
            { icon: "fas fa-refresh", label: "Refresh", onClick: () => getRolesByOrganization("") },
            { icon: "fas fa-plus", label: "Add New Role", onClick: openAddRoleModal },
          ]}
        />
        <div className="role-mgmt-body">
          <div className="role-mgmt-roles">
            <ListBox
              data={roles}
              selectedId={selectedRole?.id}
              onSelect={(node) => {
                handleListBoxSelect(node);
              }}
              actions={[
                { key: "edit", label: "Edit", icon: "fas fa-edit" },
                {
                  key: "delete",
                  label: "Delete",
                  icon: "fas fa-trash-alt",
                  danger: true,
                },
              ]}
              onAction={handleRoleAction}
            />
          </div>
          <div className="role-mgmt-detail">
            <div className="role-mgmt-detail__head">
              <div className="role-mgmt-detail__title">
                <span>{selectedRole?.title || "Select a role"}</span>
                {isSystemAdministratorRole(selectedRole?.title) ? (
                  <span className="badge bg-secondary align-middle">
                    Full access (all permissions)
                  </span>
                ) : null}
              </div>
              {selectedRole?.id && !isSystemAdministratorRole(selectedRole?.title) ? (
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={handleSavePermissions}
                >
                  Save Permissions
                </button>
              ) : null}
            </div>
            <div className="role-mgmt-detail__body">
              <UserRolePermissionPanel
                role={selectedRoleOption}
                roles={roleOptions}
                userPermissions={selectedPermissions}
                onUserPermissionsChange={handlePermissionsChange}
                userData={userData}
                canEdit={canEditRolePermissions}
                canEditRole={false}
                layout="modules"
                hideRoleDropdown
              />
            </div>
          </div>
          {selectedRole?.id ? (
            <RolePermissionSummary summary={roleSummary} />
          ) : null}
        </div>
      </div>
      {showRoleModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1100 }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add/Update Role</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeRoleModal}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-12">
                    <label htmlFor="role-mgmt-role-name">Role Name</label>
                    <input
                      id="role-mgmt-role-name"
                      type="text"
                      className={`form-control${roleNameError ? " is-invalid" : ""}`}
                      value={roleName}
                      onChange={(e) => {
                        setRoleName(e.target.value);
                        if (roleNameError) setRoleNameError("");
                      }}
                      onBlur={() => {
                        const next = normalizeRoleName(roleName);
                        if (next !== roleName) setRoleName(next);
                      }}
                    />
                    {roleNameError ? (
                      <div className="invalid-feedback d-block role-mgmt-field-error">
                        {roleNameError}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeRoleModal}
                >
                  Close
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => handleRole()}
                >
                  {isNewRole ? "Save" : "Update"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <ConfirmModal
        isOpen={Boolean(assignedUsersAlert)}
        onClose={closeAssignedUsersAlert}
        onConfirm={closeAssignedUsersAlert}
        title="Cannot delete role"
        message={
          assignedUsersAlert ? (
            <div className="role-mgmt-assigned">
              <p>
                The role "{assignedUsersAlert.role?.title}" is assigned to{" "}
                {assignedUsersAlert.users.length === 1
                  ? "this person"
                  : `these ${assignedUsersAlert.users.length} people`}
                . Reassign them before deleting the role.
              </p>
              <ul className="role-mgmt-assigned__list">
                {assignedUsersAlert.users.map((person) => (
                  <li key={person.staffId || person.email || person.name}>
                    <strong>{person.name}</strong>
                    {person.email ? <span>{person.email}</span> : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            ""
          )
        }
        confirmText="OK"
        cancelText="Close"
        confirmButtonClass="btn-primary"
        icon="fas fa-user-lock text-warning"
      />
      <ConfirmModal
        isOpen={Boolean(roleToDelete)}
        onClose={closeDeleteRoleModal}
        onConfirm={handleDeleteRole}
        title="Delete role"
        message={
          roleToDelete?.title
            ? `Are you sure you want to delete the role "${roleToDelete.title}"?`
            : "Are you sure you want to delete this role?"
        }
        confirmText="Continue"
        cancelText="Cancel"
        confirmButtonClass="btn-danger"
        icon="fas fa-trash-alt text-danger"
      />
      <InfoAlert
        isOpen={alertState.isOpen}
        onClose={hideAlert}
        {...alertState}
      />
    </>
  );
};
export default RoleManagement;
